import { useExpertUniversityStore } from "@expert/expert-university";
import { getLogger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import { HTTPError } from "ky";

import type { CreateTrainingSessionRequest, TrainingSession, UpdateTrainingSessionRequest } from "../types";
import type { TranscriptContent } from "./evaluations/types";

import { getTrainingSessionApiClient } from "./trainingSessionApi";

const logger = getLogger({ module: "Expert University API" });

interface TrainingSessionApiResult {
    count: number;
    items: TrainingSession[];
}

interface EvaluationByIdApiResult {
    count: number;
    items: TranscriptContent[];
}

const getErrorDetails = (error: unknown) => {
    if (error instanceof HTTPError) {
        return {
            error: error.message,
            statusCode: error.response.status,
            statusText: error.response.statusText,
        };
    }

    return {
        error: error instanceof Error ? error.message : String(error),
    };
};
const { sessionId: sessionIdFromStore } = useExpertUniversityStore.getState();
export const createTrainingSession = async (request: CreateTrainingSessionRequest) => {
    try {
        logger.info({ ...request, sessionId: request.SessionID }, "Creating training session");
        await getTrainingSessionApiClient()
            .extend({
                hooks: {
                    beforeRetry: [
                        ({ error, retryCount }) => {
                            logger.warn(
                                {
                                    ...getErrorDetails(error),
                                    retryCount,
                                    sessionId: request.SessionID,
                                    challengeId: request.ChallengeId,
                                },
                                "Retrying training session creation",
                            );
                        },
                    ],
                },
            })
            .post("create", {
                json: request,
            });
        trackFullStoryEvent("Training Session Created", { sessionId: request.SessionID });
    } catch (error: unknown) {
        logger.error(
            { ...getErrorDetails(error), sessionId: request.SessionID, challengeId: request.ChallengeId },
            "Error creating training session",
        );
        trackFullStoryEvent("Training Session Creation Failed", { sessionId: request.SessionID });
        throw error;
    }
};

export const updateTrainingSession = async (request: UpdateTrainingSessionRequest) => {
    const sessionId = request.SessionID;
    try {
        logger.info({ ...request, sessionId }, "Updating training session");
        trackFullStoryEvent("Training Session Updated", { sessionId });
        await getTrainingSessionApiClient().post("update", {
            json: request,
        });
    } catch (error: any) {
        logger.info({ error: error.message, sessionId }, "Error updating training session");
        trackFullStoryEvent("Training Session Update Failed", { sessionId });
    }
};

export const getTrainingSessionsByEmployeeId = async (
    employeeId: string | null,
    challengeId?: string,
    sessionId?: string,
): Promise<TrainingSessionApiResult | undefined> => {
    const taskId = sessionId ?? sessionIdFromStore;
    try {
        logger.info({ employeeId, taskId }, "Getting training sessions by employee ID");
        trackFullStoryEvent("Fetching Training Sessions", { employeeId, taskId });
        const response = await getTrainingSessionApiClient().post("query", {
            json: {
                EmployeeId: employeeId,
                ChallengeId: challengeId,
                SessionId: sessionId,
            },
        });
        const trainingSessions = await response.json<TrainingSessionApiResult>();

        return trainingSessions;
    } catch (error: any) {
        logger.info({ error: error.message, taskId }, "Error getting training sessions by employee ID");
        trackFullStoryEvent("Fetching Training Sessions Failed", { employeeId, taskId });
    }
};

export const getEvaluationResultsBySessionId = async (
    sessionId: string,
): Promise<EvaluationByIdApiResult | undefined> => {
    const taskId = sessionId ?? sessionIdFromStore;

    try {
        logger.info({ taskId }, "Getting evaluation results by session ID");
        trackFullStoryEvent("Fetching Evaluation Results", { taskId });
        const response = await getTrainingSessionApiClient().post("query", {
            json: {
                SessionId: sessionId,
            },
        });
        const trainingSession = await response.json<EvaluationByIdApiResult>();
        return trainingSession;
    } catch (error: any) {
        logger.info({ error: error.message, taskId }, "Error getting evaluation results by session ID");
        trackFullStoryEvent("Fetching Evaluation Results Failed", { taskId });
    }
};
